/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	root: {
		// We are using grid here instead of flex, so that if only a single DoDont is rendered,
		// it is aligned correctly with other rows that have multiple DoDonts.
		// It also means many DoDonts (>2) can be rendered within the same wrapper and flow onto new rows.
		display: 'grid',
		// On mobile, each element is full width.
		gridTemplateColumns: '1fr',
		'@media (min-width: 30rem)': {
			// On larger viewports, they are laid out in two columns.
			gridTemplateColumns: '1fr 1fr',
		},
		gap: token('space.200'),
	},
	paddingTop: {
		paddingBlockStart: token('space.200'),
	},
	paddingBottom: {
		paddingBlockEnd: token('space.200'),
	},
});

type DoDontGridProps = {
	children: React.ReactNode;
	/**
	 * Set to `true` to remove the default 16px top padding. Useful when the grid is placed
	 * directly underneath another element that already provides bottom spacing
	 * (e.g., another `DoDontGrid` stacked above it).
	 */
	hasNoPaddingTop?: boolean;
	/**
	 * Set to `true` to remove the default 16px bottom padding. Useful when the grid is
	 * followed by another element that already provides top spacing.
	 */
	hasNoPaddingBottom?: boolean;
};

/**
 * __DoDontGrid__
 *
 * Wrapper for DoDont components. It controls the layout of the DoDont components within the row,
 * so they are positioned side by side on small viewports, and stacked on larger viewports.
 *
 * By default the grid has 16px of vertical padding on both sides so it has breathing room
 * around surrounding content. Use `hasNoPaddingTop` / `hasNoPaddingBottom` to opt out per side.
 *
 * @example
 * ```tsx
 * <DoDontGrid>
 *   <DoDont type="do" />
 *   <DoDont type="dont" />
 * </DoDontGrid>
 *
 * // Two stacked grids without doubled spacing between them:
 * <DoDontGrid hasNoPaddingBottom>...</DoDontGrid>
 * <DoDontGrid hasNoPaddingTop>...</DoDontGrid>
 * ```
 */
export function DoDontGrid({
	children,
	hasNoPaddingTop = false,
	hasNoPaddingBottom = false,
}: DoDontGridProps): JSX.Element {
	return (
		<div
			css={[
				styles.root,
				!hasNoPaddingTop && styles.paddingTop,
				!hasNoPaddingBottom && styles.paddingBottom,
			]}
		>
			{children}
		</div>
	);
}
